import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, ArrowRight, Sparkles } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { ModeSelector } from "@/components/jam/ModeSelector";
import { LanguageToggle } from "@/components/LanguageToggle";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/i18n/I18nContext";
import { DEMO_SONGS } from "@/data/demoSongs";
import { JamTakeService } from "@/services/JamTakeService";
import { CognitiveSpaceAdapter } from "@/services/CognitiveSpaceAdapter";

const HERO =
  "https://images.unsplash.com/photo-1522008224169-e5992bed5fae?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function HomeScreen() {
  const navigate = useNavigate();
  const { mode, setMode, profile } = useApp();
  const { t, tm, dict } = useI18n();
  const takes = JamTakeService.list().slice(0, 3);
  const reflections = CognitiveSpaceAdapter.getReflections().slice(0, 2);
  const defaultSong = DEMO_SONGS[0];

  return (
    <AppShell active="home">
      <div className="mb-4 flex justify-end">
        <LanguageToggle />
      </div>

      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        className="relative overflow-hidden rounded-3xl"
        style={{ border: "1px solid rgba(255,255,255,0.08)" }}
      >
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="h-full w-full object-cover opacity-35" />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(10,12,14,0.55) 0%, rgba(10,12,14,0.85) 60%, #0A0C0E 100%)",
            }}
          />
        </div>

        <div className="relative px-6 py-10 sm:px-10 sm:py-14">
          <span className="font-mono text-[11px] uppercase tracking-[0.4em] text-amber-400/90">
            {t("home.brand")}
          </span>
          <h1 className="mt-3 whitespace-pre-line text-4xl font-semibold tracking-tight text-slate-100 sm:text-5xl">
            {t("home.heroTitle")}
          </h1>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-300 sm:text-base">
            {t("home.heroPara")}
          </p>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <button
              data-testid="start-quick-jam-btn"
              onClick={() => navigate(`/prepare/${defaultSong.id}`)}
              className="group flex items-center gap-3 rounded-full px-6 py-3.5 text-sm font-semibold text-[#1a1204] transition active:scale-95"
              style={{
                background: "linear-gradient(135deg,#FDE68A,#F59E0B 55%,#D97706)",
                boxShadow: "0 0 32px rgba(245,158,11,0.45)",
              }}
            >
              <Play size={18} fill="#1a1204" />
              {t("common.startJam")}
              <ArrowRight size={16} className="transition group-hover:translate-x-0.5" />
            </button>

            <div className="flex flex-col gap-1">
              <span className="font-mono text-[9px] uppercase tracking-widest text-slate-500">
                {t("common.guidance")}
              </span>
              <ModeSelector mode={mode} onChange={setMode} compact />
            </div>
          </div>
        </div>
      </motion.div>

      {/* My Jams */}
      <Section title={dict.home.myJams} onMore={() => navigate("/my-jams")}>
        {takes.length === 0 ? (
          <EmptyCard text={dict.home.emptyTakes} />
        ) : (
          <div className="grid gap-3 sm:grid-cols-3">
            {takes.map((take) => (
              <div
                key={take.id}
                className="rounded-2xl p-4"
                style={{ background: "rgba(18,21,25,0.8)", border: "1px solid rgba(255,255,255,0.08)" }}
              >
                <div className="text-sm font-medium text-slate-200">{take.songTitle}</div>
                <div className="text-xs text-slate-400">{take.versionName}</div>
                <div className="mt-2 flex items-center gap-2">
                  <Tag>{tm("modes", take.mode)}</Tag>
                  <Tag>{tm("progress", take.progress)}</Tag>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Training */}
      <Section title={dict.home.training}>
        <div className="grid gap-3 sm:grid-cols-3">
          {dict.home.trainingCards.map((c) => (
            <div
              key={c.t}
              className="flex flex-col gap-1 rounded-2xl p-4"
              style={{ background: "rgba(18,21,25,0.6)", border: "1px solid rgba(255,255,255,0.06)" }}
            >
              <Sparkles size={16} className="text-amber-400/80" />
              <div className="mt-1 text-sm font-medium text-slate-200">{c.t}</div>
              <div className="text-xs text-slate-400">{c.s}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Recent Sessions */}
      <Section title={dict.home.recentSessions}>
        {reflections.length === 0 ? (
          <EmptyCard
            text={t("home.profileLine", {
              level: tm("levels", profile.level),
              mode: tm("modes", profile.preferredGuidance),
              zones: profile.familiarZones.join(", "),
            })}
          />
        ) : (
          <div className="space-y-2">
            {reflections.map((r) => (
              <div
                key={r.id}
                className="rounded-2xl p-4 text-sm text-slate-300"
                style={{ background: "rgba(18,21,25,0.6)", border: "1px solid rgba(255,255,255,0.06)" }}
              >
                „{r.text}“
                <div className="mt-1 text-xs text-slate-500">
                  {new Date(r.dateISO).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </AppShell>
  );
}

const Section: React.FC<{ title: string; onMore?: () => void; children: React.ReactNode }> = ({
  title,
  onMore,
  children,
}) => {
  const { t } = useI18n();
  return (
    <div className="mt-9">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-lg font-medium tracking-tight text-slate-100">{title}</h2>
        {onMore && (
          <button
            onClick={onMore}
            className="font-mono text-[10px] uppercase tracking-widest text-amber-400/80 hover:text-amber-300"
          >
            {t("common.viewAll")}
          </button>
        )}
      </div>
      {children}
    </div>
  );
};

const EmptyCard: React.FC<{ text: string }> = ({ text }) => (
  <div
    className="rounded-2xl p-5 text-sm text-slate-400"
    style={{ background: "rgba(18,21,25,0.5)", border: "1px dashed rgba(255,255,255,0.1)" }}
  >
    {text}
  </div>
);

const Tag: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span
    className="rounded-full px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-amber-200"
    style={{ background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.2)" }}
  >
    {children}
  </span>
);
